import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import Flex from '@applied/dragonfruit/src/components/Layout/Flex';
import ConditionalLink from '@applied/dragonfruit/src/components/Link/ConditionalLink';
import { Typography } from '@applied/dragonfruit/src/components/Text/Typography';
import { useQuery } from '@tanstack/react-query';

import { BenefitsType } from 'components/WebOfferPage/benefits_classes';
import { getIcon, WebOfferIcon } from 'components/WebOfferPage/icon_requests';
import { useWebOffer } from 'components/WebOfferPage/web_offer_context';

import './benefits-section.scss';

const US_HEALTH_BENEFITS = (
  <Flex direction="column" gap="md">
    <span>
      We offer the best health insurance money can get you on the market: Anthem Platinum PPO,
      Anthem HDHP, and Kaiser HMO. Applied covers 100% of the cost and we offer medical, dental,
      vision, and life insurance.
    </span>
    <ConditionalLink link="https://drive.google.com/file/d/1qhyQVLscOvEG5v-HYgqzghNrOqMN1TQ-/view?usp=sharing">
      Learn more
    </ConditionalLink>
  </Flex>
);
const HEALTH_BENEFITS: Record<BenefitsType, string | React.ReactNode | null> = {
  [BenefitsType.US_IN_PERSON]: US_HEALTH_BENEFITS,
  [BenefitsType.US_REMOTE]: US_HEALTH_BENEFITS,
  [BenefitsType.GERMANY]: (
    <Flex direction="column" gap="md">
      <span>
        Applied pays half of your coverage payment for statutory or private health insurance
        (Krankenversicherung) and long-term care insurance (Pflegeversicherung).
      </span>
      <ConditionalLink link="https://drive.google.com/file/d/19KK5XmEmhJJxQbaQyCKm43pzF07K2APo/view?usp=drive_link">
        Learn more
      </ConditionalLink>
    </Flex>
  ),
  [BenefitsType.JAPAN]: (
    <Flex direction="column" gap="md">
      <span>
        The company will pay your Japanese National Health Insurance (Shakai Hoken”Zenkoku Kenko
        Hoken Kyokai”), National Pension (Kosei Nenkin), and Labor insurance.
      </span>
      <ConditionalLink link="https://drive.google.com/file/d/1Uo7_o4yKNw9vY-ycGGgKGezgN3eWMBhO/view?usp=drive_link">
        Learn more
      </ConditionalLink>
    </Flex>
  ),
  [BenefitsType.KOREA]: (
    <Flex direction="column" gap="md">
      <span>
        We offer supplementary life and health insurance through reimbursement of private insurance
        premiums (KRW 2,000,000 per year subject to production of supporting bills).
      </span>
      <ConditionalLink link="https://drive.google.com/file/d/1p6iRTWE97X1tAdpgpFPKBrNTB69yVFHv/view?usp=drive_link">
        Learn more
      </ConditionalLink>
    </Flex>
  ),
  [BenefitsType.SWEDEN]: (
    <Flex direction="column" gap="md">
      <span>
        Employees are automatically enrolled in mandatory public insurance via social security.
        Sweden has a comprehensive, universal public healthcare system that aims to provide
        high-quality medical services to its residents. The system is primarily funded through taxes
        and is governed by the Swedish Social Insurance Agency (Försäkringskassan).
      </span>
      <ConditionalLink link="https://drive.google.com/file/d/1vdelukw0tTZJ5jkQftnRXD8RfBDB9FvI/view?usp=drive_link">
        Learn more
      </ConditionalLink>
    </Flex>
  ),
};

const DEFAULT_PTO_BENEFITS =
  'To help you relax and unwind, you will receive 15 days of paid time off, plus federal holidays. Paid time off days increases based on tenure at the 3 year mark up to a maximum of 25 days at your 5 year mark.';
const PTO_BENEFITS: Record<BenefitsType, string | null> = {
  [BenefitsType.US_IN_PERSON]: DEFAULT_PTO_BENEFITS,
  [BenefitsType.US_REMOTE]: DEFAULT_PTO_BENEFITS,
  [BenefitsType.GERMANY]:
    'To help you relax and unwind, you will receive 30 days of paid time off per year.',
  [BenefitsType.JAPAN]: DEFAULT_PTO_BENEFITS,
  [BenefitsType.KOREA]: DEFAULT_PTO_BENEFITS,
  [BenefitsType.SWEDEN]:
    'To help you relax and unwind, you will receive 30 days of paid time off per year, plus thirteen national public holidays.',
};

type MealBenefits = {
  cutoff: string;
};
function getMealBenefitsString(benefits: MealBenefits | null): string | null {
  if (!benefits) {
    return null;
  }
  return `We expense lunch and dinner everyday if you work past ${benefits.cutoff}.`;
}
const MEAL_BENEFITS_CUTOFF: Record<BenefitsType, MealBenefits | null> = {
  [BenefitsType.US_IN_PERSON]: { cutoff: '7:30pm' },
  [BenefitsType.US_REMOTE]: null,
  [BenefitsType.GERMANY]: { cutoff: '7:30pm' },
  [BenefitsType.JAPAN]: { cutoff: '7:00pm' },
  [BenefitsType.KOREA]: { cutoff: '7:30pm' },
  [BenefitsType.SWEDEN]: { cutoff: '7:30pm' },
};

type FitnessLearningBenefits = {
  fitness: string;
  learning: string;
};
function getFitnessLearningBenefitsString(benefits: FitnessLearningBenefits | null): string | null {
  if (!benefits) {
    return null;
  }
  return `We help you invest in your physical and mental health with our annual fitness (${benefits.fitness}) and learning (${benefits.learning}) stipends.`;
}
const FITNESS_LEARNING_BENEFITS: Record<BenefitsType, FitnessLearningBenefits | null> = {
  [BenefitsType.US_IN_PERSON]: { fitness: '$600', learning: '$1,000' },
  [BenefitsType.US_REMOTE]: { fitness: '$600', learning: '$1,000' },
  [BenefitsType.GERMANY]: { fitness: '$600', learning: '$1,000' },
  [BenefitsType.JAPAN]: { fitness: '88,000 JPY', learning: '147,000 JPY' },
  [BenefitsType.KOREA]: { fitness: '$600', learning: '$1,000' },
  [BenefitsType.SWEDEN]: null,
};

const PARENTAL_BENEFITS =
  'Applied Intuition provides up to 12 weeks of fully paid parental leave for bonding with a new child, available within the first year. During leave, employees receive continued health benefits and equity vesting.';
const PARENTAL_LEAVE_BENEFITS: Record<BenefitsType, string | null> = {
  [BenefitsType.US_IN_PERSON]: PARENTAL_BENEFITS,
  [BenefitsType.US_REMOTE]: PARENTAL_BENEFITS,
  [BenefitsType.GERMANY]: PARENTAL_BENEFITS,
  [BenefitsType.JAPAN]: null,
  [BenefitsType.KOREA]: null,
  [BenefitsType.SWEDEN]:
    'Employees are entitled to a total of 480 working days of parental leave. Each parent is entitled to 240 days, and they can decide how to distribute the length up to 150 days. The Swedish Social Insurance Agency will pay the salary during this period.',
};

const COMMUTER_BENEFITS: Record<BenefitsType, string | null> = {
  [BenefitsType.US_IN_PERSON]:
    'We help you save on commute expenses. Pay for public transit with your pre-tax dollars.',
  [BenefitsType.US_REMOTE]: null,
  [BenefitsType.GERMANY]: 'We provide a subsidy for your Deutschland Ticket subscription.',
  [BenefitsType.JAPAN]:
    'To create a cleaner environment and take away the stress of commuting, we will pay for your monthly Commuter Pass (Teikiken).',
  [BenefitsType.KOREA]: null,
  [BenefitsType.SWEDEN]: null,
};

const PENSION_BENEIFTS: Record<BenefitsType, string | null> = {
  [BenefitsType.US_IN_PERSON]: null,
  [BenefitsType.US_REMOTE]: null,
  [BenefitsType.GERMANY]: null,
  [BenefitsType.JAPAN]:
    'Applied Intuition contributes 27,500 JPY every month for each employee through an/the SBI benefit. You can also contribute a maximum of 27,500 JPY per month. This is a non-taxable amount, which helps you save on taxes.',
  [BenefitsType.KOREA]: null,
  [BenefitsType.SWEDEN]:
    'Comprehensive savings and pension plan for working employees through the State pension to fund their retirement in the future.',
};

const US_RETIREMENT_BENEFITS =
  'Applied Intuition will match 50% of the IRS limit for individual contributions up to the first 50% deferred. Your personal contributions and company match are immediately vested at 100%.';
const RETIREMENT_BENEFITS: Record<BenefitsType, string | null> = {
  [BenefitsType.US_IN_PERSON]: US_RETIREMENT_BENEFITS,
  [BenefitsType.US_REMOTE]: US_RETIREMENT_BENEFITS,
  [BenefitsType.GERMANY]: null,
  [BenefitsType.JAPAN]: null,
  [BenefitsType.KOREA]: null,
  [BenefitsType.SWEDEN]: null,
};

const HOUSING_BENEFITS: Record<BenefitsType, string | null> = {
  [BenefitsType.US_IN_PERSON]: null,
  [BenefitsType.US_REMOTE]: null,
  [BenefitsType.GERMANY]: null,
  [BenefitsType.JAPAN]:
    'Through our pre-tax rent deduction, Applied Intuition can sign your rental contract directly with the real estate company, and your rent will be deducted from your salary before tax, reducing your taxable income.',
  [BenefitsType.KOREA]: null,
  [BenefitsType.SWEDEN]: null,
};

const LANGUAGE_BENEFITS: Record<BenefitsType, string | null> = {
  [BenefitsType.US_IN_PERSON]: null,
  [BenefitsType.US_REMOTE]: null,
  [BenefitsType.GERMANY]: null,
  [BenefitsType.JAPAN]:
    'We’re offering a one-time 290,000 JPY stipend for you to use on a 3-month English or Japanese language learning program.',
  [BenefitsType.KOREA]: null,
  [BenefitsType.SWEDEN]: null,
};

const US_MENTAL_HEALTH_SUPPORT_BENEFITS =
  'You and your dependents receive 25 free coaching or therapy sessions per person per year at no cost to you through Lyra, plus access to personalized care plans, coaching, therapy, and wellness tools.';
const MENTAL_HEALTH_SUPPORT_BENEFITS: Record<BenefitsType, string | null> = {
  [BenefitsType.US_IN_PERSON]: US_MENTAL_HEALTH_SUPPORT_BENEFITS,
  [BenefitsType.US_REMOTE]: US_MENTAL_HEALTH_SUPPORT_BENEFITS,
  [BenefitsType.GERMANY]: null,
  [BenefitsType.JAPAN]: null,
  [BenefitsType.KOREA]: null,
  [BenefitsType.SWEDEN]: null,
};

type Benefits = {
  header: string;
  icon: string | null;
  description: string | React.ReactNode | null;
};
export function BenefitsSection(): JSX.Element {
  const { benefitsType } = useWebOffer();
  const history = useHistory();

  const { data: healthInsuranceIconResponse } = useQuery({
    queryKey: ['icon', WebOfferIcon.HEALTH_INSURANCE],
    queryFn: () => getIcon(WebOfferIcon.HEALTH_INSURANCE, history),
    enabled: benefitsType !== null && HEALTH_BENEFITS[benefitsType] !== null,
  });

  const { data: paidTimeOffIconResponse } = useQuery({
    queryKey: ['icon', WebOfferIcon.PTO],
    queryFn: () => getIcon(WebOfferIcon.PTO, history),
    enabled: benefitsType !== null && PTO_BENEFITS[benefitsType] !== null,
  });

  const { data: lunchDinnerIconResponse } = useQuery({
    queryKey: ['icon', WebOfferIcon.LUNCH_DINNER],
    queryFn: () => getIcon(WebOfferIcon.LUNCH_DINNER, history),
    enabled:
      benefitsType !== null && getMealBenefitsString(MEAL_BENEFITS_CUTOFF[benefitsType]) !== null,
  });

  const { data: fitnessLearningIconResponse } = useQuery({
    queryKey: ['icon', WebOfferIcon.FITNESS_LEARNING],
    queryFn: () => getIcon(WebOfferIcon.FITNESS_LEARNING, history),
    enabled: benefitsType !== null && FITNESS_LEARNING_BENEFITS[benefitsType] !== null,
  });

  const { data: parentalLeaveIconResponse } = useQuery({
    queryKey: ['icon', WebOfferIcon.PARENTAL_LEAVE],
    queryFn: () => getIcon(WebOfferIcon.PARENTAL_LEAVE, history),
    enabled: benefitsType !== null && PARENTAL_LEAVE_BENEFITS[benefitsType] !== null,
  });

  const { data: commuterBenefitsIconResponse } = useQuery({
    queryKey: ['icon', WebOfferIcon.COMMUTER],
    queryFn: () => getIcon(WebOfferIcon.COMMUTER, history),
    enabled: benefitsType !== null && COMMUTER_BENEFITS[benefitsType] !== null,
  });

  const { data: pensionBenefitsIconResponse } = useQuery({
    queryKey: ['icon', WebOfferIcon.PENSION],
    queryFn: () => getIcon(WebOfferIcon.PENSION, history),
    enabled:
      benefitsType !== null &&
      (PENSION_BENEIFTS[benefitsType] !== null || RETIREMENT_BENEFITS[benefitsType] !== null),
  });

  const { data: companyHousingIconResponse } = useQuery({
    queryKey: ['icon', WebOfferIcon.HOUSING],
    queryFn: () => getIcon(WebOfferIcon.HOUSING, history),
    enabled: benefitsType !== null && HOUSING_BENEFITS[benefitsType] !== null,
  });

  const { data: languageProgramIconResponse } = useQuery({
    queryKey: ['icon', WebOfferIcon.LANGUAGE],
    queryFn: () => getIcon(WebOfferIcon.LANGUAGE, history),
    enabled: benefitsType !== null && LANGUAGE_BENEFITS[benefitsType] !== null,
  });

  const { data: heartIconResponse } = useQuery({
    queryKey: ['icon', WebOfferIcon.HEART],
    queryFn: () => getIcon(WebOfferIcon.HEART, history),
    enabled: benefitsType !== null && MENTAL_HEALTH_SUPPORT_BENEFITS[benefitsType] !== null,
  });

  const benefits: Benefits[] = useMemo(() => {
    if (!benefitsType) {
      return [];
    }

    return [
      {
        header: 'Health insurance',
        icon: healthInsuranceIconResponse?.iconUrl ?? null,
        description: HEALTH_BENEFITS[benefitsType],
      },
      {
        header: 'Paid time off',
        icon: paidTimeOffIconResponse?.iconUrl ?? null,
        description: PTO_BENEFITS[benefitsType],
      },
      {
        header: 'Lunches and dinner',
        icon: lunchDinnerIconResponse?.iconUrl ?? null,
        description: getMealBenefitsString(MEAL_BENEFITS_CUTOFF[benefitsType]),
      },
      {
        header: 'Fitness and learning stipends',
        icon: fitnessLearningIconResponse?.iconUrl ?? null,
        description: getFitnessLearningBenefitsString(FITNESS_LEARNING_BENEFITS[benefitsType]),
      },
      {
        header: 'Parental leave',
        icon: parentalLeaveIconResponse?.iconUrl ?? null,
        description: PARENTAL_LEAVE_BENEFITS[benefitsType],
      },
      {
        header: 'Commuter benefits',
        icon: commuterBenefitsIconResponse?.iconUrl ?? null,
        description: COMMUTER_BENEFITS[benefitsType],
      },
      {
        header: 'Pension contributions',
        icon: pensionBenefitsIconResponse?.iconUrl ?? null,
        description: PENSION_BENEIFTS[benefitsType],
      },
      {
        header: 'Retirement contributions',
        icon: pensionBenefitsIconResponse?.iconUrl ?? null,
        description: RETIREMENT_BENEFITS[benefitsType],
      },
      {
        header: 'Company housing program',
        icon: companyHousingIconResponse?.iconUrl ?? null,
        description: HOUSING_BENEFITS[benefitsType],
      },
      {
        header: 'Language program',
        icon: languageProgramIconResponse?.iconUrl ?? null,
        description: LANGUAGE_BENEFITS[benefitsType],
      },
      {
        header: 'Mental health support',
        icon: heartIconResponse?.iconUrl ?? null,
        description: MENTAL_HEALTH_SUPPORT_BENEFITS[benefitsType],
      },
    ];
  }, [
    benefitsType,
    healthInsuranceIconResponse,
    paidTimeOffIconResponse,
    lunchDinnerIconResponse,
    fitnessLearningIconResponse,
    parentalLeaveIconResponse,
    commuterBenefitsIconResponse,
    pensionBenefitsIconResponse,
    companyHousingIconResponse,
    languageProgramIconResponse,
    heartIconResponse,
  ]);

  if (!benefitsType) {
    return <Typography variant="body">Loading...</Typography>;
  }

  return (
    <Flex
      className="benefits-section-container"
      direction="column"
      gap="lg"
      alignment="center"
      justify="center"
      padding="xxl"
    >
      <div id="benefits">
        <Flex className="benefits-section" direction="column" gap="xxl">
          <Flex className="benefits-section-header" direction="column">
            <Typography variant="title">2026 BENEFITS</Typography>
            <Typography variant="header">From paid meals to top tier health insurance</Typography>
          </Flex>
          <div className="benefits-grid">
            {benefits
              .filter((benefit) => benefit.description)
              .map((benefit) => (
                <Flex key={benefit.header} className="benefit-item" direction="column" gap="md">
                  {benefit.icon && (
                    <img className="benefit-icon" src={benefit.icon} alt={benefit.header} />
                  )}
                  <Typography variant="subtitle">{benefit.header}</Typography>
                  <Typography variant="body">{benefit.description}</Typography>
                </Flex>
              ))}
          </div>
        </Flex>
      </div>
    </Flex>
  );
}
